import {
  AudioBlobRecorder,
  AudioBlobRecorderEvents,
  AzureSTT,
  AzureSTTParseFileResponse,
} from '@mavatar/melingo-voice-and-avatar-sdk';
import classNames from './PronunciationMicrophone.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Question } from '@/types/question';
import commonUtils from '@/utils/common';
import { useDispatch } from 'react-redux';
import { gameActions, gameSelector } from '@/store/reducers/game';
import { GameStatus, NewGameTypes } from '@/types/game';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

import AppLoaderCircle from '@/components/AppLoaderCircle';
import AnswerReward from '../../AnswerReward/AnswerReward';
import AnalyzingSentences from './AnalyzingSentences';
import TheButton from '@/components/TheButton/TheButton';

import MicrophonePermissionsStyles from '@/components/microphone-permissions/MicrophonePermissionsInfo.module.scss';
import MicrophonePermissionsInfo, {
  MicrophoneErrorTypes,
} from '@/components/microphone-permissions/MicrophonePermissionsInfo';

import microphone from '@/assets/svg/microphone.svg';
import check from '@/assets/svg/check.svg';
import wrong from '@/assets/svg/wrong.svg';
import wrongmic from '@/assets/svg/wrongmic.svg';
import UserUtils from '@/utils/user';
import UseAzureLanguageSettings from '@/hooks/UseAzureLanguageSettings';

const MAX_RETRY_COUNT = 2;
const MAX_EMPTY_SPEECH_COUNT = 3;
const MAX_MIC_PERMISSIONS_INFO_SHOW_COUNT = 2;
const PASSING_SCORE = 4.5;
const RECORD_TIMEOUT = 30000;

interface PronunciationMicrophoneProps {
  currentQuestion: Question;
  pickedAnswer?: string;
  emitOnRetry?: () => void;
  emitOnAnswer: (option: string) => void;
  emitOnNextQuestion?: () => void;
  checkIfIsListening: (isListening: boolean) => void;
}

const PronunciationMicrophone = ({
  currentQuestion,
  pickedAnswer,
  emitOnRetry,
  emitOnAnswer,
  emitOnNextQuestion,
  checkIfIsListening,
}: PronunciationMicrophoneProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { user } = useAppSelector(authSelector);

  const isStudent = UserUtils.isStudent(user);

  const [showFeedback, setShowFeedback] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [userSpeechEmptyCounter, setUserSpeechEmptyCounter] = useState(0);
  const [retryCounter, setRetryCounter] = useState(0);

  const allowRetry =
    retryCounter < MAX_RETRY_COUNT && userSpeechEmptyCounter < MAX_EMPTY_SPEECH_COUNT;

  const questionTitle = currentQuestion?.question?.toLowerCase();

  const { checkAnswerCompleted, isFinalAssessment, gameType } = useAppSelector(gameSelector);

  const isNaturalConversationType = gameType === NewGameTypes.NaturalConversation;

  const { sttLanguage } = UseAzureLanguageSettings();
  const audioBlobRecorderRef = useRef<AudioBlobRecorder>(
    new AudioBlobRecorder({ recordTimeout: RECORD_TIMEOUT })
  );
  const azureSTTRef = useRef<AzureSTT>(
    new AzureSTT({
      language: sttLanguage,
    })
  );

  const [permissionError, setPermissionError] = useState<MicrophoneErrorTypes | null>(null);
  const [permissionErrorCounter, setPermissionErrorCounter] = useState(0);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [isUserSpeechEmpty, setIsUserSpeechEmpty] = useState<boolean>(false);

  useEffect(() => {
    let isActive = true;

    setIsListening(false);
    setIsUserSpeechEmpty(false);
    setUserSpeechEmptyCounter(0);
    setRetryCounter(0);
    setIsCorrect(false);
    setShowFeedback(false);
    setShowOverlay(false);
    setPermissionError(null);
    setPermissionErrorCounter(0);
    setIsProcessing(false);

    const onAudioBlobRecorderStart = async () => {
      if (!isActive) {
        return;
      }

      setIsListening(true);
    };

    const onAudioBlobRecorderStop = async () => {
      if (!isActive) {
        return;
      }

      setIsListening(false);
    };

    const onAudioBlobRecorderDataReady = async () => {
      if (!isActive) {
        return;
      }

      setIsProcessing(true);

      let processingData: AzureSTTParseFileResponse | null = null;

      try {
        const audio = await audioBlobRecorderRef.current.base64;
        if (audio) {
          processingData = await azureSTTRef.current.parseFile(audio, questionTitle);
        }
      } catch (error) {}

      if (!isActive) {
        return;
      }

      if (!processingData) {
        processingData = {
          text: '',
          detectedText: '',
          originalDetectedText: '',
          isCorrect: false,
          options: [
            {
              confidence: 1,
              text: '',
            },
          ],
        };
      }

      const isUserSpeechEmpty = !processingData?.detectedText;
      setIsUserSpeechEmpty(isUserSpeechEmpty);

      if (!isUserSpeechEmpty) {
        // const isCorrect = !!(
        //   processingData.text === processingData.detectedText ||
        //   (processingData.text &&
        //     processingData.options?.length > 0 &&
        //     processingData.options[0].score &&
        //     processingData.options[0].score.AccuracyScore >= PASSING_SCORE &&
        //     processingData.options[0].score.CompletenessScore >= PASSING_SCORE &&
        //     processingData.options[0].score.FluencyScore >= PASSING_SCORE &&
        //     processingData.options[0].score.PronScore >= PASSING_SCORE) ||
        //   commonUtils.isSimilarPhrases(questionTitle, processingData.detectedText.toLowerCase())
        // );
        //
        // const resultText = isCorrect
        //   ? processingData.text || processingData?.detectedText
        //   : processingData?.detectedText;

        const isCorrect = !!(processingData as any).isCorrect;
        const resultText = isCorrect ? questionTitle : processingData.detectedText;

        setIsCorrect(isCorrect);
        setShowFeedback(true);

        dispatch(gameActions.toggleGameStatus(GameStatus.QUESTION_ANSWERED));
        dispatch(gameActions.setPickedAnswer({ answer: resultText }));

        if (!isCorrect) {
          return;
        }
      }

      setIsProcessing(false);
    };

    const onAudioBlobRecorderError = (error: any) => {
      if (!isActive) {
        return;
      }

      setIsListening(false);

      if (error) {
        switch (error.name) {
          case 'NotAllowedError':
            setPermissionError(MicrophoneErrorTypes.permissions);
            break;
          case 'NotFoundError':
          case 'OverconstrainedError':
          case 'TypeError':
            setPermissionError(MicrophoneErrorTypes.device_not_found);
            break;
          case 'NotReadableError':
          case 'InvalidStateError':
            setPermissionError(MicrophoneErrorTypes.device_in_use);
            break;
          default:
            setPermissionError(MicrophoneErrorTypes.failed_to_access_the_device);
            break;
        }
      }

      setIsUserSpeechEmpty(true);
      audioBlobRecorderRef.current.stop();
    };

    audioBlobRecorderRef.current.dispatcher.addListener(
      AudioBlobRecorderEvents.START,
      onAudioBlobRecorderStart
    );
    audioBlobRecorderRef.current.dispatcher.addListener(
      AudioBlobRecorderEvents.STOP,
      onAudioBlobRecorderStop
    );
    audioBlobRecorderRef.current.dispatcher.addListener(
      AudioBlobRecorderEvents.DATA_READY,
      onAudioBlobRecorderDataReady
    );
    audioBlobRecorderRef.current.dispatcher.addListener(
      AudioBlobRecorderEvents.ERROR,
      onAudioBlobRecorderError
    );

    return () => {
      isActive = false;

      audioBlobRecorderRef.current.dispatcher.removeListener(
        AudioBlobRecorderEvents.START,
        onAudioBlobRecorderStart
      );
      audioBlobRecorderRef.current.dispatcher.removeListener(
        AudioBlobRecorderEvents.STOP,
        onAudioBlobRecorderStop
      );
      audioBlobRecorderRef.current.dispatcher.removeListener(
        AudioBlobRecorderEvents.DATA_READY,
        onAudioBlobRecorderDataReady
      );
      audioBlobRecorderRef.current.dispatcher.removeListener(
        AudioBlobRecorderEvents.ERROR,
        onAudioBlobRecorderError
      );

      dispatch(gameActions.setPauseTimer(false));
    };
  }, [questionTitle]);

  const onToggle = () => {
    if (audioBlobRecorderRef.current.isRecording) {
      audioBlobRecorderRef.current.stop();
    } else {
      audioBlobRecorderRef.current.start();
    }
  };

  const onAnswer = () => {
    dispatch(gameActions.toggleGameStatus(GameStatus.QUESTION_ANSWERED));
    dispatch(gameActions.setPickedAnswer({ answer: '' }));
    emitOnAnswer('');

    emitOnNextQuestion?.();
  };

  const onRetry = () => {
    setRetryCounter(retryCounter => retryCounter + 1);
    emitOnRetry?.();
    dispatch(gameActions.onPlay());
    setShowOverlay(false);
  };

  useEffect(() => {
    const isPause =
      permissionError !== null || isUserSpeechEmpty || showFeedback || showOverlay || isProcessing;
    dispatch(gameActions.setPauseTimer(isPause));
  }, [dispatch, permissionError, isUserSpeechEmpty, showFeedback, showOverlay, isProcessing]);

  useEffect(() => {
    if (isUserSpeechEmpty) {
      setUserSpeechEmptyCounter(pref => pref + 1);
    }
  }, [isUserSpeechEmpty]);

  const postCheckAnswer = async () => {
    if (isCorrect) {
      await commonUtils.sleep(1000);
      setShowFeedback(false);
      setIsProcessing(false);
      emitOnNextQuestion?.();
      return;
    }

    setShowFeedback(false);
    setIsProcessing(false);

    if (!isFinalAssessment) {
      setShowOverlay(true);
    }
  };

  useEffect(() => {
    if (checkAnswerCompleted) {
      postCheckAnswer();
    }
  }, [checkAnswerCompleted]);

  const displayOverlay = async () => {
    if (pickedAnswer) {
      emitOnAnswer(pickedAnswer);
    }
  };

  useEffect(() => {
    if (showFeedback) {
      displayOverlay();
    }
  }, [showFeedback]);

  useEffect(() => {
    checkIfIsListening(isListening);
  }, [isListening]);

  const nextQuestionBtn = (
    <TheButton
      className={classNames.nextQuestion}
      emitOnClick={onAnswer}
      aria-label={t('skipThisQuestion').toString()}
      text={t('skipThisQuestion')}
      shouldRecolor={false}
      showArrow={true}
    />
  );

  const feedback = (
    <div
      className={classes(classNames.feedback, {
        [classNames.feedbackCorrect]: isCorrect,
      })}
    >
      <span>{isCorrect ? t('goodJob') : t('incorrect')}</span>
      <div className={classNames.circle}>
        <SVG src={isCorrect ? check : wrong} />
      </div>
    </div>
  );

  const overlay = (
    <div className={classNames.overlayWrapper}>
      <div className={classNames.errorMessage}>
        <div className={classNames.errorMessageTitel}>
          {t('theAnswerIsIncorrectWhatWeHeardWas')}
        </div>
        <div className={classNames.errorMessageContainer}>
          <SVG src={wrongmic} />
          <span>{`"${pickedAnswer}"`}</span>
        </div>
      </div>

      <div className={classNames.overlay}>
        <div className={classNames.buttons}>
          {allowRetry && (
            <TheButton
              className={classNames.retry}
              emitOnClick={onRetry}
              aria-label={t('tryAgain').toString()}
              text={t('tryAgain')}
            />
          )}
          {nextQuestionBtn}
        </div>
      </div>
    </div>
  );

  const emptySpeechOverlay = (
    <div className={classNames.overlayWrapper}>
      <div className={classNames.errorMessage}>
        <div className={classNames.errorMessageContainer}>
          <SVG src={wrongmic} />
          <span>{t('didntQuiteHearThat')}</span>
        </div>
      </div>

      <div className={classNames.overlay}>
        <div className={classNames.buttons}>
          {allowRetry && (
            <TheButton
              className={classNames.retry}
              emitOnClick={() => {
                setIsUserSpeechEmpty(false);
              }}
              aria-label={t('tryAgain').toString()}
              text={t('tryAgain')}
            />
          )}
          {nextQuestionBtn}
        </div>
      </div>
    </div>
  );

  const permissionOverlay =
    permissionErrorCounter < MAX_MIC_PERMISSIONS_INFO_SHOW_COUNT ? (
      <div data-hide_quiz_pause_modal={true} className={classNames.overlayWrapper}>
        <div className={MicrophonePermissionsStyles.permissionOverlay}>
          <div className={MicrophonePermissionsStyles.permissionContent}>
            {permissionError && (
              <MicrophonePermissionsInfo
                onClose={() => {
                  setIsUserSpeechEmpty(false);
                  setPermissionError(null);
                  setPermissionErrorCounter(counter => counter + 1);
                }}
                skipAllSpeechRecordingQuestions={() => {
                  dispatch(gameActions.setSkipSpeechRecordingQuestions(true));
                }}
                fromQuiz={true}
                errorType={permissionError}
              />
            )}
          </div>
        </div>
      </div>
    ) : (
      <div className={classNames.overlayWrapper}>
        <div className={classNames.errorMessage}>
          <div className={classNames.errorMessageContainer}>
            <SVG src={wrongmic} />
            <span>{t('microphoneRestricted')}</span>
          </div>
        </div>

        <div className={classNames.overlay}>
          <div className={classNames.buttons}>{nextQuestionBtn}</div>
        </div>
      </div>
    );

  let body = (
    <button
      className={classes(classNames.speakButton, {
        [classNames.speakPulse]: isListening,
      })}
      onClick={onToggle}
    >
      {!isListening && <SVG src={microphone} />}
      {isListening && <span></span>}
    </button>
  );

  let bottom: ReactElement<any, any> | null = !showOverlay ? (
    <span
      className={classes(classNames.text, {
        [classNames.textPulse]: isListening,
      })}
    >
      {isListening ? t('listeningTapToFinish') : t('tapToSpeak')}
    </span>
  ) : null;

  if (permissionError) {
    body = permissionOverlay;
    bottom = null;
  } else if (isUserSpeechEmpty) {
    body = emptySpeechOverlay;
    bottom = null;
  } else if (showOverlay) {
    body = overlay;
    bottom = null;
  } else if (isProcessing) {
    body = <AppLoaderCircle className={classNames.loader} />;
    bottom = (
      <span className={classNames.text}>
        <AnalyzingSentences />
      </span>
    );
  } else if (showFeedback) {
    body = feedback;
    bottom =
      isStudent &&
      isCorrect &&
      currentQuestion.coins &&
      !currentQuestion.userData?.achievedCoins ? (
        <AnswerReward className={classNames.answerReward} coins={currentQuestion.coins} />
      ) : null;
  }

  return (
    <div className={classNames.pronunciationMicrophone}>
      <div className={classNames.body}>{body}</div>
      {bottom && <div className={classNames.bottom}>{bottom}</div>}
    </div>
  );
};

export default React.memo(PronunciationMicrophone);
